/**
 * Cross-user audit access (FE helpers for Backend CrossUserAccess).
 * Login Capabilities.CanEditOthersAudits + audit/task IsReadOnly / ReadOnlyReason.
 */

import {
    POST_COMPLETE_EDIT_DISABLED_CODE,
    POST_COMPLETE_EDIT_DISABLED_MESSAGE,
} from './template-setting-utility'

export const AUDIT_READ_ONLY_CODE = 'AUDIT_READ_ONLY'

export const READ_ONLY_REASON = {
    NOT_OWNER: 'NOT_OWNER',
    COMPLETED_LOCKED: 'COMPLETED_LOCKED',
    DIFFERENT_TENANT: 'DIFFERENT_TENANT',
}

/**
 * Customer Claim (129321) and Customer Claim Non RMA (170965).
 * These audits are a shared multi-user workflow, so CrossUserAccess
 * owner-lock (IsReadOnly + NOT_OWNER) must not apply.
 */
export const CROSS_USER_ACCESS_EXEMPT_ORIGINAL_TEMPLATE_IDS = [
    129321,
    170965,
]

const CROSS_USER_ACCESS_EXEMPT_TEMPLATE_NAMES = [
    'customer claim',
    'customer claim non rma',
    'quality non rma',
]

/** Catalog TemplateId from GET /InspectionTask list (not the per-audit GUID). */
export const CROSS_USER_ACCESS_EXEMPT_TEMPLATE_GUIDS = [
    '507df0dc-f126-4a43-8cae-248a5c88d1c3', // Customer Claim
    '2afb0941-b389-45a8-9e83-b3064a2f157d', // Customer Claim Non RMA
]

function normalizeExemptTemplateName(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
}

function normalizeExemptGuid(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
}

function isExemptCustomerClaimName(value) {
    const name = normalizeExemptTemplateName(value)
    if (!name) return false
    if (CROSS_USER_ACCESS_EXEMPT_TEMPLATE_NAMES.includes(name)) return true
    if (name.startsWith('customer claim non rma')) return true
    if (name.startsWith('customer claim -')) return true
    if (name.startsWith('quality non rma')) return true
    return false
}

function isExemptCustomerClaimGuid(value) {
    const guid = normalizeExemptGuid(value)
    return !!guid && CROSS_USER_ACCESS_EXEMPT_TEMPLATE_GUIDS.includes(guid)
}

export function isCrossUserAccessExemptAudit(audit) {
    if (!audit) return false
    const numericIds = [
        audit.OriginalTemplateId,
        audit.originalTemplateId,
        audit.TemplateUniqueId,
        audit.templateUniqueId,
        audit.CustomerTemplateId,
        audit.customerTemplateId,
        audit.TemplateNumericId,
        audit.templateNumericId,
    ]
    if (
        numericIds.some(id =>
            CROSS_USER_ACCESS_EXEMPT_ORIGINAL_TEMPLATE_IDS.includes(Number(id)),
        )
    ) {
        return true
    }
    if (
        isExemptCustomerClaimGuid(audit.TemplateId) ||
        isExemptCustomerClaimGuid(audit.templateId)
    ) {
        return true
    }
    return (
        isExemptCustomerClaimName(audit.TemplateName) ||
        isExemptCustomerClaimName(audit.templateName) ||
        isExemptCustomerClaimName(audit.AuditName) ||
        isExemptCustomerClaimName(audit.auditName) ||
        isExemptCustomerClaimName(audit.Title) ||
        isExemptCustomerClaimName(audit.title)
    )
}

function isCompletedLockReason(reason) {
    return (
        reason === READ_ONLY_REASON.COMPLETED_LOCKED ||
        reason === POST_COMPLETE_EDIT_DISABLED_CODE
    )
}

export const AUDIT_NOT_FOUND_MESSAGE =
    "Audit not found or you don't have access"

function parseBool(value, fallback = false) {
    if (value === undefined || value === null) return fallback
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase()
        if (['1', 'true', 'yes', 'y'].includes(v)) return true
        if (['0', 'false', 'no', 'n'].includes(v)) return false
    }
    return fallback
}

/** Admins: IsSuperAdmin / IsAdmin / RoleId 3|1000 — also Capabilities.CanEditOthersAudits. */
export function canEditOthersAudits(user) {
    const caps = user?.Capabilities
    if (caps != null && caps.CanEditOthersAudits !== undefined) {
        return parseBool(caps.CanEditOthersAudits, false)
    }
    if (user?.IsSuperAdmin || user?.IsAdmin) return true
    const roleId = Number(user?.RoleId)
    return roleId === 3 || roleId === 1000
}

function getCallerUserId(user) {
    if (!user) return null
    const id =
        user.unique_id ??
        user.UniqueId ??
        user.UserId ??
        user.userId ??
        user.Id
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
}

function getTaskOwnerUserId(task) {
    if (!task) return null
    const id =
        task.AuditorUserId ??
        task.auditorUserId ??
        task.OwnerUserId ??
        task.ownerUserId ??
        task.UserId ??
        task.CreatedBy
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Prefer API `IsReadOnly`. If omitted, treat non-owned rows as read-only
 * for callers who cannot edit others' audits (typical after kill-switch ON).
 * Admins with CanEditOthersAudits never get the View-only chip from this fallback.
 */
export function isTaskReadOnly(task, user) {
    if (!task) return false

    if (
        isCrossUserAccessExemptAudit(task) &&
        !isCompletedLockReason(
            task.ReadOnlyReason ?? task.readOnlyReason,
        )
    ) {
        return false
    }

    const raw = task.IsReadOnly ?? task.isReadOnly
    if (raw !== undefined && raw !== null && raw !== '') {
        return parseBool(raw, false)
    }

    if (!user || canEditOthersAudits(user)) return false

    const ownerId = getTaskOwnerUserId(task)
    const callerId = getCallerUserId(user)
    if (!ownerId || !callerId) return false
    return ownerId !== callerId
}

export function isAuditReadOnly(audit) {
    const flagged = parseBool(audit?.IsReadOnly ?? audit?.isReadOnly, false)
    if (!flagged) return false
    if (!isCrossUserAccessExemptAudit(audit)) return true
    return isCompletedLockReason(
        audit?.ReadOnlyReason ?? audit?.readOnlyReason,
    )
}

/**
 * Customer Service Claim Portal templates are a shared multi-user workflow.
 * POST /Inspection must not echo the normal-template owner lock
 * (IsReadOnly + NOT_OWNER). Completed-audit lock stays in place.
 */
export function applyCrossUserAccessSaveExemption(audit) {
    if (!audit || typeof audit !== 'object') return audit
    if (!isCrossUserAccessExemptAudit(audit)) return audit
    const reason = getAuditReadOnlyReason(audit)
    if (isCompletedLockReason(reason)) return audit
    if (reason === READ_ONLY_REASON.DIFFERENT_TENANT) return audit
    const flagged = parseBool(audit.IsReadOnly ?? audit.isReadOnly, false)
    if (!flagged && reason !== READ_ONLY_REASON.NOT_OWNER) return audit
    return {
        ...audit,
        IsReadOnly: false,
        ReadOnlyReason: null,
    }
}

export function readAuditAccessRequestBody(raw) {
    if (!raw) return null
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw)
            return parsed && typeof parsed === 'object' ? parsed : null
        } catch {
            return null
        }
    }
    if (typeof raw === 'object') return raw
    return null
}

/** Owner-lock toast is for normal templates. Claim portal saves skip it. */
export function shouldSuppressClaimOwnerLockToast(requestData, responseData, code) {
    if (code === POST_COMPLETE_EDIT_DISABLED_CODE) return false
    const reason =
        getAuditReadOnlyReason(responseData) ||
        getAuditReadOnlyReason(requestData)
    if (isCompletedLockReason(reason)) return false
    if (reason === READ_ONLY_REASON.DIFFERENT_TENANT) return false
    const body = readAuditAccessRequestBody(requestData)
    return isCrossUserAccessExemptAudit(body)
}

export function getAuditOwnerName(auditOrError) {
    return (
        auditOrError?.OwnerName ||
        auditOrError?.ownerName ||
        auditOrError?.Owner ||
        ''
    )
}

export function getAuditReadOnlyReason(auditOrError) {
    return (
        auditOrError?.ReadOnlyReason ||
        auditOrError?.readOnlyReason ||
        null
    )
}

/** User-facing message when audit/task is view-only. */
export function getAuditReadOnlyMessage({
    reason,
    ownerName,
    message,
} = {}) {
    if (message) return message
    const owner = (ownerName || '').trim()
    if (
        reason === READ_ONLY_REASON.COMPLETED_LOCKED ||
        reason === POST_COMPLETE_EDIT_DISABLED_CODE
    ) {
        return POST_COMPLETE_EDIT_DISABLED_MESSAGE
    }
    if (
        reason === READ_ONLY_REASON.NOT_OWNER ||
        reason === AUDIT_READ_ONLY_CODE ||
        owner
    ) {
        return owner
            ? `You cannot edit this audit — it was created by ${owner}.`
            : 'You cannot edit this audit — it is owned by another user.'
    }
    return 'You cannot edit this audit.'
}

export function getAuditAccessErrorCode(data) {
    if (!data || typeof data !== 'object') return null
    return (
        data.code ||
        data.Code ||
        data.errorCode ||
        data.ErrorCode ||
        null
    )
}

export function isAuditAccessErrorCode(code) {
    return (
        code === AUDIT_READ_ONLY_CODE ||
        code === POST_COMPLETE_EDIT_DISABLED_CODE
    )
}

/**
 * Parse 403 bodies from gated write endpoints.
 * @returns {{ isAuditAccessError, code, message, ownerName, readOnlyReason }}
 */
export function parseAuditAccessError(err) {
    const status = err?.status
    const body = err?.error
    const code = getAuditAccessErrorCode(body) || err?.code
    const ownerName = getAuditOwnerName(body)
    const readOnlyReason = getAuditReadOnlyReason(body)
    const rawMessage =
        body?.message || body?.Message || err?.message || null

    const isAuditAccessError =
        status === 403 && isAuditAccessErrorCode(code)

    const message = isAuditAccessError
        ? getAuditReadOnlyMessage({
              reason: readOnlyReason || code,
              ownerName,
              message: rawMessage,
          })
        : null

    return {
        isAuditAccessError,
        code: isAuditAccessError ? code : null,
        message,
        ownerName,
        readOnlyReason,
    }
}
